/**
 * AEC (Acoustic Echo Cancellation) Test and Configuration Module
 * 
 * This module provides functionality to test and configure AEC settings
 * for optimal echo prevention during AI speech playback.
 */

class AECTester {
    constructor() {
        this.testResults = {};
        this.currentStream = null;
    }

    /**
     * Test AEC functionality by playing a test tone and monitoring microphone input
     */
    async testAEC() {
        console.log("🔧 Starting AEC test...");
        
        try {
            // Get microphone access with AEC constraints
            const constraints = {
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                    sampleRate: 16000,
                    channelCount: 1
                }
            };

            this.currentStream = await navigator.mediaDevices.getUserMedia(constraints);
            const track = this.currentStream.getAudioTracks()[0];
            const settings = track.getSettings();
            
            this.testResults = {
                echoCancellation: settings.echoCancellation,
                noiseSuppression: settings.noiseSuppression,
                autoGainControl: settings.autoGainControl,
                sampleRate: settings.sampleRate,
                channelCount: settings.channelCount,
                deviceId: settings.deviceId
            };

            console.log("✅ AEC Test Results:", this.testResults);
            
            // Create audio context for analysis
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const source = audioContext.createMediaStreamSource(this.currentStream);
            const analyser = audioContext.createAnalyser();
            
            source.connect(analyser);
            analyser.fftSize = 256;
            
            const dataArray = new Uint8Array(analyser.frequencyBinCount);
            
            // Monitor for 3 seconds
            const startTime = Date.now();
            const monitorDuration = 3000;
            
            const monitor = () => {
                analyser.getByteFrequencyData(dataArray);
                const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
                
                if (Date.now() - startTime < monitorDuration) {
                    console.log("🎤 Audio level:", average.toFixed(2));
                    requestAnimationFrame(monitor);
                } else {
                    this.cleanup();
                    console.log("✅ AEC test completed");
                }
            };
            
            monitor();
            
            return this.testResults;
            
        } catch (error) {
            console.error("❌ AEC test failed:", error);
            this.cleanup();
            throw error;
        }
    }

    /**
     * Get optimal AEC constraints based on device capabilities
     */
    async getOptimalConstraints() {
        const supportedConstraints = navigator.mediaDevices.getSupportedConstraints();
        
        const constraints = {
            audio: {
                echoCancellation: supportedConstraints.echoCancellation || true,
                noiseSuppression: supportedConstraints.noiseSuppression || true,
                autoGainControl: supportedConstraints.autoGainControl || true,
                sampleRate: 16000,  // Optimal for speech recognition
                channelCount: 1     // Mono is sufficient for speech
            }
        };

        // Test different constraint combinations to find what works best
        const strategies = [
            constraints,
            { ...constraints, audio: { ...constraints.audio, echoCancellation: false } },
            { audio: true } // Fallback
        ];

        for (const strategy of strategies) {
            try {
                const testStream = await navigator.mediaDevices.getUserMedia(strategy);
                const settings = testStream.getAudioTracks()[0].getSettings();
                testStream.getTracks().forEach(track => track.stop());
                
                console.log("✅ Optimal AEC constraints found:", settings);
                return strategy;
            } catch (error) {
                console.warn("AEC strategy failed, trying next:", error);
            }
        }

        throw new Error("No working AEC constraints found");
    }

    /**
     * Clean up resources
     */
    cleanup() {
        if (this.currentStream) {
            this.currentStream.getTracks().forEach(track => track.stop());
            this.currentStream = null;
        }
    }

    /**
     * Display AEC status in the UI
     */
    displayStatus(container) {
        if (!container) return;

        const statusHtml = `
            <div class="aec-status">
                <h4>🔧 AEC Configuration</h4>
                <ul>
                    <li>Echo Cancellation: ${this.testResults.echoCancellation ? '✅ Active' : '❌ Inactive'}</li>
                    <li>Noise Suppression: ${this.testResults.noiseSuppression ? '✅ Active' : '❌ Inactive'}</li>  
                    <li>Auto Gain Control: ${this.testResults.autoGainControl ? '✅ Active' : '❌ Inactive'}</li>
                    <li>Sample Rate: ${this.testResults.sampleRate} Hz</li>
                    <li>Channels: ${this.testResults.channelCount}</li>
                </ul>
            </div>
        `;

        container.innerHTML = statusHtml;
    }
}

// Export for use in other modules
window.AECTester = AECTester;

// Auto-initialize if needed
document.addEventListener('DOMContentLoaded', () => {
    console.log("🔧 AEC Test module loaded");
});